import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface,
  DialogTitle, DialogTrigger, Text, Tooltip,
} from '@fluentui/react-components';
import type { CSSProperties } from 'react';
import type { SelectedEntity } from '../../api/types';

interface EntitySyncOptionsDialogProps {
  selection: SelectedEntity;
  title: string;
  summary: ReactNode;
  configureLabel?: string;
  configureAriaLabel?: string;
  configureDescription?: string;
  configureStyle?: CSSProperties;
  disabled?: boolean;
  doneDisabled?: (draft: SelectedEntity) => boolean;
  confirmDraft?: (draft: SelectedEntity) => SelectedEntity | null;
  reconcileVersion?: unknown;
  reconcileDraft?: (draft: SelectedEntity, previousVersion: unknown) => SelectedEntity;
  onOpenChange?: (open: boolean) => void;
  onDone: (selection: SelectedEntity) => void;
  children: (draft: SelectedEntity, onChange: (selection: SelectedEntity) => void) => ReactNode;
}

function cloneSelection(selection: SelectedEntity): SelectedEntity {
  return { ...selection, identity_key_columns: selection.identity_key_columns?.slice() };
}

export function EntitySyncOptionsDialog({
  selection: savedSelection, title, summary, configureLabel = 'Configure', disabled = false,
  configureAriaLabel, configureDescription, configureStyle, doneDisabled, confirmDraft,
  reconcileVersion, reconcileDraft, onOpenChange, onDone, children,
}: EntitySyncOptionsDialogProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SelectedEntity | null>(null);
  const reconcileRef = useRef(reconcileDraft);
  const previousReconcileVersion = useRef(reconcileVersion);
  reconcileRef.current = reconcileDraft;

  useEffect(() => {
    const previousVersion = previousReconcileVersion.current;
    previousReconcileVersion.current = reconcileVersion;
    setDraft(current => current && reconcileRef.current
      ? reconcileRef.current(current, previousVersion) : current);
  }, [reconcileVersion]);

  const changeOpen = (nextOpen: boolean) => {
    setDraft(nextOpen ? cloneSelection(savedSelection) : null);
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };
  const selection = draft ?? savedSelection;
  const handleDone = () => {
    if (disabled || doneDisabled?.(selection)) return;
    const confirmed = confirmDraft ? confirmDraft(selection) : selection;
    if (!confirmed) return;
    onDone(confirmed);
    changeOpen(false);
  };

  return (
    <div onClick={event => event.stopPropagation()} className="polling-summary">
      <Text size={200}>{summary}</Text>
      <Dialog open={open} onOpenChange={(_, data) => changeOpen(data.open)}>
        <Tooltip content={configureDescription ?? title} relationship="description">
          <DialogTrigger disableButtonEnhancement>
            <Button size="small" appearance="subtle" disabled={disabled}
              aria-label={configureAriaLabel} style={configureStyle}>{configureLabel}</Button>
          </DialogTrigger>
        </Tooltip>
        <DialogSurface className="polling-dialog">
          <DialogBody>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>{children(selection, setDraft)}</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => changeOpen(false)}>Cancel</Button>
              <Button appearance="primary" disabled={disabled || doneDisabled?.(selection)}
                onClick={handleDone}>Done</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}